import React from "react";
import {
  X,
  MonitorX,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Clock,
  ImageOff,
  FileImage,
  ChevronLeft,
  ChevronRight,
  Trash2,
  FolderOpen,
  Pencil,
} from "lucide-react";



import {
  UploadPanel,
  UploadPanelHeader,
  SectionTitle,
  SecondaryBtn,
  PollErrorBanner,
  UploadStatsBar,
  StatChip,
  PollingBadge,
  ExpiryInfo,
  LoadingRow,
  Spinner,
  EmptyUploads,
  UploadTableWrap,
  UploadTable,
  Th,
  Tr,
  Td,
  StatusBadge,
  MediaIdChip,
  PaginationRow,
  PageLabel,
  RevokeSection,
  DangerBtn,
  ChangeDestinationBtn,
  PanelDestinationBar,
  PanelDestinationInfo,
} from "./CameraToCloudStyles";
import { C2CSession, C2CUploadLog } from "../../../../Redux/ApiCalls/Dashboard/C2CAPI";

interface UploadProgressPanelProps {
  activeSessionId: number;
  activeSession: C2CSession | null;
  pollError: string | null;
  uploadLoading: boolean;
  uploadLogs: C2CUploadLog[];
  uploadPage: number;
  setUploadPage: React.Dispatch<React.SetStateAction<number>>;
  totalUploadPages: number;
  onClose: () => void;
  onRevoke: (sessionId: number) => void;
  onEditDestination?: (session: C2CSession) => void;
  getExpiryInfo: (expiresAt?: string | null) => {
    label: string;
    isWarning: boolean;
    isExpired: boolean;
    hasExpiry: boolean;
  };
}



const formatBytes = (bytes: number): string => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const UploadProgressPanel: React.FC<UploadProgressPanelProps> = ({
  activeSessionId,
  activeSession,
  pollError,
  uploadLoading,
  uploadLogs,
  uploadPage,
  setUploadPage,
  totalUploadPages,
  onClose,
  onRevoke,
  onEditDestination,
  getExpiryInfo,
}) => {
  return (
    <UploadPanel>
      <UploadPanelHeader>
        <SectionTitle style={{ margin: 0 }}>Upload Progress</SectionTitle>
        <SecondaryBtn id="c2c-close-upload-view" onClick={onClose}>
          <X size={14} /> Close
        </SecondaryBtn>
      </UploadPanelHeader>

      {pollError && (
        <PollErrorBanner>
          <MonitorX size={18} /> {pollError}
        </PollErrorBanner>
      )}

      {activeSession && (
        <>
          <PanelDestinationBar>
            <PanelDestinationInfo>
              <FolderOpen size={16} color="#c94daa" />
              <div>
                <span>Destination: </span>
                <strong>{activeSession.album?.name || "—"}</strong> / {activeSession.folder?.name || "—"}
              </div>
            </PanelDestinationInfo>
            {activeSession.status === "active" && onEditDestination && (
              <ChangeDestinationBtn
                id="c2c-panel-edit-session"
                type="button"
                onClick={() => onEditDestination(activeSession)}
              >
                <Pencil size={12} /> Edit Session
              </ChangeDestinationBtn>
            )}

          </PanelDestinationBar>

          <UploadStatsBar>
            <StatChip $color="#16a34a" style={{ fontSize: 14, padding: "6px 14px" }}>
              <CheckCircle2 size={15} /> {activeSession.upload_stats.success} uploaded
            </StatChip>
            {activeSession.upload_stats.failed > 0 && (
              <StatChip $color="#ef4444" style={{ fontSize: 14, padding: "6px 14px" }}>
                <AlertCircle size={15} /> {activeSession.upload_stats.failed} failed
              </StatChip>
            )}
            {!pollError && (
              <PollingBadge>
                <RefreshCw size={12} /> Live — updating every 5s
              </PollingBadge>
            )}
            {!pollError && getExpiryInfo(activeSession.expires_at).hasExpiry && (
              <ExpiryInfo $warning={getExpiryInfo(activeSession.expires_at).isWarning}>
                <Clock size={13} />
                {getExpiryInfo(activeSession.expires_at).label}
              </ExpiryInfo>
            )}


          </UploadStatsBar>
        </>
      )}


      {uploadLoading ? (
        <LoadingRow>
          <Spinner size={20} />
          <span>Loading uploads…</span>
        </LoadingRow>
      ) : uploadLogs.length === 0 ? (
        <EmptyUploads>
          <ImageOff size={40} color="#c94daa" strokeWidth={1.5} />
          <p>No uploads yet. Configure your camera and start shooting!</p>
        </EmptyUploads>
      ) : (
        <>
          <UploadTableWrap>
            <UploadTable>
              <thead>
                <tr>
                  <Th>Filename</Th>
                  <Th>Size</Th>
                  <Th>Status</Th>
                  <Th>Error</Th>
                  <Th>Media ID</Th>
                  <Th>Time</Th>
                </tr>
              </thead>
              <tbody>
                {uploadLogs.map((log) => (
                  <Tr key={log.id}>
                    <Td>
                      <FileImage size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
                      {log.source_filename}
                    </Td>
                    <Td>{formatBytes(log.file_size_bytes)}</Td>
                    <Td>
                      <StatusBadge $status={log.status}>{log.status}</StatusBadge>
                    </Td>
                    <Td style={{ color: "#ef4444", fontSize: 12 }}>
                      {log.error_message || "—"}
                    </Td>
                    <Td>{log.media_id ? <MediaIdChip>#{log.media_id}</MediaIdChip> : "—"}</Td>
                    <Td style={{ fontSize: 12, color: "rgba(23,23,23,0.5)" }}>
                      {new Date(log.created_at).toLocaleTimeString()}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </UploadTable>
          </UploadTableWrap>
          {totalUploadPages > 1 && (
            <PaginationRow>
              <SecondaryBtn
                id="c2c-prev-page"
                disabled={uploadPage <= 1}
                onClick={() => setUploadPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft size={16} />
              </SecondaryBtn>
              <PageLabel>
                Page {uploadPage} of {totalUploadPages}
              </PageLabel>
              <SecondaryBtn
                id="c2c-next-page"
                disabled={uploadPage >= totalUploadPages}
                onClick={() => setUploadPage((p) => Math.min(totalUploadPages, p + 1))}
              >
                <ChevronRight size={16} />
              </SecondaryBtn>
            </PaginationRow>
          )}
        </>
      )}

      {!pollError && activeSession?.status === "active" && (
        <RevokeSection>
          <DangerBtn
            id="c2c-revoke-from-panel"
            onClick={() => onRevoke(activeSessionId)}
          >
            <Trash2 size={14} /> Revoke Session
          </DangerBtn>
        </RevokeSection>
      )}
    </UploadPanel>
  );
};
